import PropTypes from "prop-types";

import { makeStyles } from "@mui/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Button, Menu, MenuItem, CircularProgress } from "@mui/material";
import { isDeletedBid, isPendingBid } from "../../util/bid/bidCommon";
import { useState } from "react";
import { exportToCsv, generateExportedFilename } from "../../util/bid/bidGroupExport";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper";
import { GET_BID_SENIORITY_LIST_TYPE } from "../../util/constants";
import {
  fetchLatestBidChoicesAndExportToCsv,
  generateShiftBidEmployeeChoicesExportedFilename,
  generateTimeoffBidEmployeeChoicesExportedFilename,
} from "../../util/bid/empBidChoicesExport";
import { getNonRemovedEmployees } from "../../util/bid/bidGroupUtils";
import { BID_TYPE } from "../../util/bid/bidConstants";

const useStyles = makeStyles((theme) => ({
  exportButton: {
    marginRight: "10px",
    height: "40px",
  },
}));

const BidGroupExportCsv = (props) => {
  const classes = useStyles();

  // export
  const [exportsMenuAnchor, setExportsMenuAnchor] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportBtnClick = (event) => {
    setExportsMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportsMenuAnchor(null);
  };

  const handleExportBidGroupClick = () => {
    try {
      exportToCsv({
        filename: generateExportedFilename({
          station: props.bid.station,
          workgroup: props.bid.workgroup.replace(/\s/g, "_"),
          fileType: "csv",
          bidType: props.bid.bidType,
        }),
        rows: props.bidGroup,
        contract: getUnionContractGivenWorkgroup(props.bid.workgroup),
        seniorityListType: GET_BID_SENIORITY_LIST_TYPE(
          getUnionContractGivenWorkgroup(props.bid.workgroup),
          props.bid.station,
          props.bid.bidType
        ),
        workgroup: props.bid.workgroup,
        bidType: props.bid.bidType,
        station: props.bid.station,
      });
    } catch (e) {
      console.error("Error during downloading csv!");
    }
  };

  const exportShiftBidChoices = () => {
    setExportLoading(true);
    try {
      fetchLatestBidChoicesAndExportToCsv({
        filename: generateShiftBidEmployeeChoicesExportedFilename({
          station: props.bid.station,
          workgroup: props.bid.workgroup.replace(/\s/g, "_"),
          fileType: "csv",
        }),
        rows: getNonRemovedEmployees(props.bidGroup),
        referenceNumber: props.bid.referenceNumber,
        bidType: props.bid.bidType,
        station: props.bid.station,
      }).then(() => setExportLoading(false));
    } catch (e) {
      setExportLoading(false);
      console.error("Error during downloading csv!");
    }
  };

  const exportTimeoffBidChoices = () => {
    setExportLoading(true);
    try {
      fetchLatestBidChoicesAndExportToCsv({
        filename: generateTimeoffBidEmployeeChoicesExportedFilename({
          bidType: props.bid.bidType.replace(/\s/g, "_"),
          schedMonth: props.bid.schedMonth,
          schedYear: props.bid.schedYear,
          station: props.bid.station,
          workgroup: props.bid.workgroup.replace(/\s/g, "_"),
          fileType: "csv",
        }),
        rows: getNonRemovedEmployees(props.bidGroup),
        referenceNumber: props.bid.referenceNumber,
        bidType: props.bid.bidType,
        station: props.bid.station,
      }).then(() => setExportLoading(false));
    } catch (e) {
      setExportLoading(false);
      console.error("Error during downloading csv!");
    }
  };

  // NOSONAR
  const handleExportChoicesClick = () => {
    switch (props.bid.bidType) {
      case BID_TYPE.SHIFT:
        exportShiftBidChoices();
        break;
      case BID_TYPE.FREEDAY:
      case BID_TYPE.FTO:
      case BID_TYPE.TWU_DAT_FTO:
      case BID_TYPE.IAM_DAT:
      case BID_TYPE.NC_PTO_FTO:
      case BID_TYPE.NC_PTO_GOC_HOL:
        exportTimeoffBidChoices();
        break;
      default:
        console.error("Invalid Bid Type");
    }
  };

  const renderBidGroupExportCsvButton = () => {
    return (
      <>
        <Button
          data-testid="bidGroup-export-button"
          className={classes.exportButton}
          variant="contained"
          color="primary"
          size="medium"
          onClick={handleExportBtnClick}
          startIcon={exportLoading ? <CircularProgress size={20} color="secondary" /> : <UploadFileIcon />}
          disabled={!props.bidGroup.length}
        >
          Export CSV
        </Button>
        <Menu
          anchorEl={exportsMenuAnchor}
          open={!!exportsMenuAnchor}
          onClose={handleExportMenuClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleExportBidGroupClick}>Bid Group CSV</MenuItem>
          <MenuItem
            onClick={handleExportChoicesClick}
            disabled={exportLoading || isDeletedBid(props.bid.status) || isPendingBid(props.bid.status)}
          >
            Employee Choices CSV
          </MenuItem>
        </Menu>
      </>
    );
  };
  return <div>{renderBidGroupExportCsvButton()}</div>;
};

BidGroupExportCsv.propTypes = {
  bidGroup: PropTypes.array.isRequired,
  bid: PropTypes.object.isRequired,
};

export default BidGroupExportCsv;
